/**
 * usePullToRefresh — touch-only pull-to-refresh primitive.
 *
 * Listens to global touch events and, when the user pulls down at the
 * very top of the page (`window.scrollY === 0`), reports the current
 * pull distance and fires `onRefresh` once the pull crosses
 * `threshold`. Designed to feel like the native iOS / Material
 * pattern — applies progressive resistance so the indicator never
 * runs away from the user.
 *
 * Returns `{ pullDistance, refreshing, supported }`. The caller
 * renders an indicator using `pullDistance` for the partial-pull
 * state and `refreshing` for the spinner state. On desktop pointer
 * environments (`pointer: fine`) the hook reports `supported=false`
 * and never fires — pull-to-refresh has no UX value with a mouse
 * wheel.
 */
import { useEffect, useRef, useState } from 'react';

const PULL_RESISTANCE = 0.5; // 1.0 = 1:1 with finger; lower = stiffer
const PULL_MAX_OVERSHOOT = 1.6; // can over-pull to 1.6× threshold for spring feel

function isTouchEnv() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return !window.matchMedia('(pointer: fine)').matches;
}

export default function usePullToRefresh({
  onRefresh,
  threshold = 70,
  enabled = true,
} = {}) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [supported] = useState(() => isTouchEnv());

  // Refs mirror state for the listener closure. setState alone would
  // be captured stale on the first effect run.
  const refreshingRef = useRef(false);
  const distanceRef = useRef(0);
  const startYRef = useRef(null);

  useEffect(() => { refreshingRef.current = refreshing; }, [refreshing]);

  useEffect(() => {
    if (!enabled || !supported) return undefined;

    const onStart = (e) => {
      // Only arm when at the very top and idle. Touch events at
      // mid-scroll should never trigger the refresh affordance.
      if (window.scrollY > 0 || refreshingRef.current) return;
      startYRef.current = e.touches[0]?.clientY ?? null;
    };

    const onMove = (e) => {
      if (startYRef.current == null || refreshingRef.current) return;
      const dy = (e.touches[0]?.clientY ?? 0) - startYRef.current;
      if (window.scrollY === 0 && dy > 0) {
        const distance = Math.min(dy * PULL_RESISTANCE, threshold * PULL_MAX_OVERSHOOT);
        distanceRef.current = distance;
        setPullDistance(distance);
        // Cancel native overscroll so the page doesn't fight the
        // indicator. ≥8px deadzone prevents stealing tap-then-scroll.
        if (dy > 8 && e.cancelable) e.preventDefault();
      } else if (dy <= 0) {
        // User reversed direction → abort the pull cleanly.
        startYRef.current = null;
        distanceRef.current = 0;
        setPullDistance(0);
      }
    };

    const onEnd = async () => {
      const distance = distanceRef.current;
      startYRef.current = null;
      distanceRef.current = 0;
      if (distance >= threshold && !refreshingRef.current) {
        setRefreshing(true);
        try {
          await onRefresh?.();
        } catch {
          // Swallow — the calling page is responsible for surfacing
          // its own error state. We just unstick the spinner.
        } finally {
          setRefreshing(false);
        }
      }
      setPullDistance(0);
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd, { passive: true });
    window.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [enabled, supported, threshold, onRefresh]);

  return { pullDistance, refreshing, supported, threshold };
}
